import {
  applyPeoplePackageToMock,
  createAppService,
  ServiceError,
  type AppData,
  type AppDataStore,
  type AppService,
} from '@lg/core';
import {
  MockD365Client,
  createHybridD365Client,
  type D365Client,
  type MockState,
  type MockStateStore,
} from '@lg/d365-client';
import { MockDirectoryClient } from '@lg/directory-client';
import { findPersona, type CurrentUser, type EnvProfile, type StepKey } from '@lg/shared';
import { safeStorage } from '../storage';
import { ApiError, type ApiClient } from './client';
import { createRemoteD365Client } from './remoteD365';

/**
 * In-browser backend for the static demo build and browser-mode dev. The same
 * app service and mock D365 the API uses, with per-viewer state kept in
 * localStorage. Mock mode only — there is no real data here.
 */

const APP_KEY = 'lg.appData.v1';
const D365_KEY = 'lg.mockD365.v1';

class LocalJson<T> {
  private memory: T | null = null;
  constructor(private readonly key: string) {}
  load(): T | null {
    const raw = safeStorage.get(this.key);
    if (raw) {
      try {
        return JSON.parse(raw) as T;
      } catch {
        return this.memory;
      }
    }
    return this.memory;
  }
  save(value: T): void {
    this.memory = value;
    safeStorage.set(this.key, JSON.stringify(value));
  }
}

let servicePromise: Promise<AppService> | null = null;

function getService(liveSteps: readonly StepKey[], envProfile?: EnvProfile): Promise<AppService> {
  servicePromise ??= (async () => {
    mockD365 = new MockD365Client({
      store: new LocalJson<MockState>(D365_KEY) satisfies MockStateStore,
      // Simulated D365 jobs (copy, people package) finish in a few seconds, so they don't
      // stand out next to the live steps.
      copySeconds: 3,
      // A processed people package lands in mock D365 so the report reads it back.
      packageApplier: applyPeoplePackageToMock,
    });
    // Live slice: named steps go to the sandbox through /api/d365; the rest stay simulated.
    const d365: D365Client = liveSteps.length
      ? createHybridD365Client({
          live: createRemoteD365Client(),
          mock: mockD365,
          liveSteps,
          onMirrorError: (method, err) => console.info(`Mock mirror of ${method} skipped:`, err),
        })
      : mockD365;
    const service = createAppService({
      store: new LocalJson<AppData>(APP_KEY) satisfies AppDataStore,
      d365,
      directory: new MockDirectoryClient(),
      pollMs: 1000,
      timeoutMs: 10 * 60_000,
      liveSteps,
      ...(envProfile ? { envProfile } : {}),
    });
    await service.ensureSeeded();
    return service;
  })();
  return servicePromise;
}

let mockD365: MockD365Client | null = null;

/** Fired on window when another tab changed the demo data; the UI refetches. */
export const DEMO_DATA_CHANGED = 'lg:demo-data-changed';

// Each tab holds the demo data in memory and writes all of it back on save. When another tab
// (a persona opened from the run sheet, say) saves or resets, rebuild from storage before the next
// call; otherwise this tab's next save would overwrite the other tab's requests.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== APP_KEY && e.key !== D365_KEY && e.key !== null) return;
    servicePromise = null;
    window.dispatchEvent(new Event(DEMO_DATA_CHANGED));
  });
}

export function resetDemoData(): void {
  safeStorage.remove(APP_KEY);
  safeStorage.remove(D365_KEY);
  servicePromise = null;
}

function toApiError(err: unknown): unknown {
  if (err instanceof ServiceError) {
    return new ApiError({ type: 'about:blank', title: err.title, status: err.status, detail: err.message });
  }
  return err;
}

export function createBrowserApiClient(
  getAuthHeaders: () => Promise<Record<string, string>>,
  options: { liveSteps?: readonly StepKey[]; envProfile?: EnvProfile } = {},
): ApiClient {
  const liveSteps = options.liveSteps ?? [];
  const svc = (): Promise<AppService> => getService(liveSteps, options.envProfile);
  const currentUser = async (): Promise<CurrentUser> => {
    const headers = await getAuthHeaders();
    const persona = findPersona(headers['x-mock-user'] ?? '');
    if (!persona) throw new ApiError({ type: 'about:blank', title: 'Unauthorized', status: 401 });
    return persona;
  };

  const base: Pick<ApiClient, 'health' | 'me' | 'resetDemo' | 'demoFailure'> = {
    demoFailure: {
      get: async () => {
        await svc();
        return mockD365?.pendingFailure ?? null;
      },
      set: async (target) => {
        await svc();
        mockD365?.setFailOnce(target);
      },
    },
    health: async () => ({
      status: 'ok',
      d365Mode: 'mock',
      directoryMode: 'mock',
      authMode: 'mock',
      time: new Date().toISOString(),
    }),
    me: currentUser,
    resetDemo: resetDemoData,
  };

  return new Proxy(base, {
    get(target, prop: string) {
      if (prop in target) return target[prop as keyof typeof target];
      return async (...args: unknown[]) => {
        const [service, user] = await Promise.all([svc(), currentUser()]);
        const fn = service[prop as keyof AppService] as unknown;
        if (typeof fn !== 'function') throw new Error(`Unknown API method ${prop}`);
        try {
          const result = await (fn as (u: CurrentUser, ...a: unknown[]) => Promise<unknown>)(user, ...args);
          // Copy like an HTTP response would: the UI must never hold live store objects
          // (in-place updates would look unchanged to React Query's structural sharing).
          return structuredClone(result);
        } catch (err) {
          throw toApiError(err);
        }
      };
    },
  }) as unknown as ApiClient;
}
